import { useState, useCallback, useMemo, useRef } from 'react';
import { getTabForHash, TabType } from '../routing/parseHashRoute';

export type { TabType };
export type NavigationDirection = 'forward' | 'back' | 'none';

const TAB_ORDER: Record<TabType, number> = {
  home: 0,
  goals: 1,
  ongoing: 2,
  timely: 3,
  achievements: 4,
};

// Route depth hierarchy for direction detection
function getRouteDepth(hash: string): number {
  const path = hash.replace('#', '') || '/';
  if (/^\/subgoals\/[^/]+\/todos\/[^/]+$/.test(path)) return 4; // todo-detail
  if (/^\/subgoals\/[^/]+\/todos$/.test(path)) return 3;        // subgoal-todos
  if (/^\/goals\/[^/]+$/.test(path)) return 2;                  // goal-detail
  if (path === '/profile') return 1;
  // Tab-level pages: /home, /goals, /ongoing, /timely
  return 0;
}

function getRouteKey(hash: string): string {
  const path = hash.replace('#', '') || '/home';
  // Normalize top-level routes
  if (path === '/' || path === '') return '/home';
  return path;
}

interface UseNavigationReturn {
  /** Derived from the current hash; null on pages outside the tab bar. */
  activeTab: TabType | null;
  navigateToTab: (tab: TabType) => void;
  navigateToGoal: (goalId: string) => void;
  navigateToSubGoalTodos: (subGoalId: string) => void;
  goBack: () => void;
  navigationDirection: NavigationDirection;
  routeKey: string;
  computeDirection: (newHash: string) => NavigationDirection;
  setNavigationDirection: (dir: NavigationDirection) => void;
}

export const useNavigation = (): UseNavigationReturn => {
  const [currentHash, setCurrentHash] = useState(() => window.location.hash);
  const activeTab = useMemo(() => getTabForHash(currentHash), [currentHash]);
  const [navigationDirection, setNavigationDirection] = useState<NavigationDirection>('none');
  const [routeKey, setRouteKey] = useState(() => getRouteKey(window.location.hash));
  const prevHashRef = useRef(window.location.hash);
  const routeHistoryRef = useRef<string[]>([window.location.hash]);
  const isGoingBackRef = useRef(false);

  const computeDirection = useCallback((newHash: string): NavigationDirection => {
    const prev = prevHashRef.current;
    if (prev === newHash) return 'none';

    let direction: NavigationDirection;

    // If goBack was explicitly called
    if (isGoingBackRef.current) {
      isGoingBackRef.current = false;
      direction = 'back';
    } else {
      const prevDepth = getRouteDepth(prev);
      const newDepth = getRouteDepth(newHash);

      // Deeper route = forward, shallower = back
      if (newDepth > prevDepth) {
        direction = 'forward';
      } else if (newDepth < prevDepth) {
        direction = 'back';
      } else {
        // Same depth — tab-to-tab transition: use tab order
        const prevTab = prev.replace('#/', '') as TabType;
        const newTab = newHash.replace('#/', '') as TabType;
        if (TAB_ORDER[prevTab] !== undefined && TAB_ORDER[newTab] !== undefined) {
          direction = TAB_ORDER[newTab] > TAB_ORDER[prevTab] ? 'forward' : 'back';
        } else {
          direction = 'forward';
        }
      }
    }

    // Update route history and key
    if (direction === 'back') {
      if (routeHistoryRef.current.length > 1) routeHistoryRef.current.pop();
    } else {
      routeHistoryRef.current.push(newHash);
    }
    prevHashRef.current = newHash;
    setCurrentHash(newHash);
    setRouteKey(getRouteKey(newHash));

    return direction;
  }, []);

  const navigateToTab = useCallback((tab: TabType) => {
    const hashMap: Record<TabType, string> = {
      home: '#/home',
      goals: '#/goals',
      ongoing: '#/ongoing',
      timely: '#/timely',
      achievements: '#/achievements',
    };
    window.location.hash = hashMap[tab] || '#/home';
  }, []);

  const navigateToGoal = useCallback((goalId: string) => {
    window.location.hash = `#/goals/${goalId}`;
  }, []);

  const navigateToSubGoalTodos = useCallback((subGoalId: string) => {
    window.location.hash = `#/subgoals/${subGoalId}/todos`;
  }, []);

  const goBack = useCallback(() => {
    isGoingBackRef.current = true;
    const history = routeHistoryRef.current;

    if (history.length > 1) {
      // Navigate to previous route in our stack
      const parentHash = history[history.length - 2];
      window.location.hash = parentHash;
    } else {
      // Fallback: smart parent detection from current hash
      const currentPath = window.location.hash.replace('#', '') || '/';
      let parentHash = '#/home';

      if (/^\/subgoals\/[^/]+\/todos\/[^/]+$/.test(currentPath)) {
        // todo-detail → subgoal-todos
        const subgoalId = currentPath.match(/^\/subgoals\/([^/]+)\/todos/)?.[1];
        parentHash = subgoalId ? `#/subgoals/${subgoalId}/todos` : '#/goals';
      } else if (/^\/subgoals\/[^/]+\/todos$/.test(currentPath)) {
        // subgoal-todos → goals (we don't know the goal ID easily)
        parentHash = '#/goals';
      } else if (/^\/goals\/[^/]+$/.test(currentPath)) {
        // goal-detail → goals
        parentHash = '#/goals';
      } else if (currentPath === '/profile') {
        parentHash = '#/home';
      }

      window.location.hash = parentHash;
    }
  }, []);

  return {
    activeTab,
    navigateToTab,
    navigateToGoal,
    navigateToSubGoalTodos,
    goBack,
    navigationDirection,
    routeKey,
    computeDirection,
    setNavigationDirection,
  };
};
